import type { PersonRecord } from "../../../server/core/people";
import { foldRecord } from "../../ui/fold";
import { openViewer } from "../../ui/viewer";
import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { chip } from "../../ui/record";
import { sendButton } from "../../ui/send";
import { area, field, refuse, select } from "../clients/forms";
import { inboxChanged } from "./inbox";

// Inbox › Documents, one upload waiting for review (brief 1B-3, mockup inbox documents): the file,
// its number and dates to type from it (AI reading is off), and Approve, Send back (reject with a
// reason) or Waive with a reason, exactly as on the person record (the same routes, the same
// checks), so readiness updates. Background checks and drug screens show their file and result
// only to people with the switch.

interface DocView {
  id: string;
  person: { id: string; name: string; firstName: string; path: string | null };
  business: string | null;
  doc: {
    code: string;
    name: string;
    kind: string;
    expires: boolean;
    restricted: boolean;
    status: string;
    waiting: boolean;
    number: string | null;
    issuedOn: string | null;
    expiresOn: string | null;
    file: { id: string | null; name: string } | null;
    uploaded: string;
  };
  canReview: boolean;
  seeResults: boolean;
}

const BACK_REASONS = ["Blurry or cut off", "Wrong document", "Expired", "Name doesn't match"];

export async function documentScreen(host: HTMLElement, ctx: ScreenContext, id: string) {
  const res = await api<DocView>("GET", `/api/inbox/documents/${encodeURIComponent(id)}`);
  if (!res.ok) {
    host.append(
      el(
        "div",
        { class: "blank", role: "alert" },
        icon("circle-slash", "big"),
        el("h2", {}, "Not available"),
        el("p", { class: "muted" }, "This document isn't here, or this role can't review it."),
      ),
    );
    return;
  }
  const v = res.body;
  const d = v.doc;
  ctx.crumb(v.person.name);
  const base = `/api/people/${v.person.id}/documents/${d.code}`;

  const number = field("Number", d.number, { maxlength: "60" });
  const issued = field("Issued", d.issuedOn, { type: "date" });
  const expires = field("Expires", d.expiresOn, { type: "date" });
  const result =
    d.restricted && v.seeResults
      ? select(
          "Result",
          [
            ["", "–"],
            ["clear", "Clear"],
            ["not_clear", "Not clear"],
          ],
          "",
        )
      : null;
  const note = el("div", { class: "note", role: "alert", hidden: true });

  /** After a review: says whether the person is ready now, then back to Documents. */
  const reviewed = (r: { ok: boolean; body: PersonRecord & { error?: string } }, did: string) => {
    if (!r.ok) return refuse(note, r.body.error);
    inboxChanged();
    toast(
      `${d.name} ${did}`,
      r.body.readiness.ready
        ? `${v.person.firstName} is Ready`
        : `${v.person.firstName}'s checklist updated · not ready yet`,
    );
    ctx.go("/inbox/docs");
  };
  const post = (path: string, body: unknown) =>
    api<PersonRecord>("POST", `${base}/${path}`, body) as Promise<{
      ok: boolean;
      body: PersonRecord & { error?: string };
    }>;

  const approve = el(
    "button",
    { type: "button", class: "btn pri" },
    icon("check-circle"),
    " Approve",
  );
  approve.addEventListener("click", async () => {
    approve.disabled = true;
    const r = await post("approve", {
      number: number.input.value || null,
      issuedOn: issued.input.value || null,
      expiresOn: expires.input.value || null,
      ...(result ? { result: result.input.value || null } : {}),
    });
    approve.disabled = false;
    reviewed(r, "approved");
  });

  const back = el(
    "button",
    { type: "button", class: "btn pri" },
    icon("paper-airplane"),
    " Send back",
  );
  back.addEventListener("click", () => {
    const radios = BACK_REASONS.map((r, i) =>
      el(
        "label",
        { class: "ptask" },
        el("input", { type: "radio", name: "dnr", value: r, checked: i === 0 }),
        ` ${r}`,
      ),
    );
    const more = area("More for them (optional)", "");
    const go = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("paper-airplane"),
      " Send back",
    );
    const m = openModal({
      title: "Send it back",
      sub: `${v.person.name} · ${d.name}. They get a reminder with the reason.`,
      body: [el("div", { class: "form", style: "max-width:none" }, ...radios, more.el)],
      actions: [go],
    });
    go.addEventListener("click", async () => {
      const picked =
        m.root.querySelector<HTMLInputElement>('input[name="dnr"]:checked')?.value ?? "";
      const extra = more.input.value.trim();
      go.disabled = true;
      const r = await post("reject", { reason: extra ? `${picked}: ${extra}` : picked });
      go.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      reviewed(r, "sent back");
    });
  });

  const waive = el("button", { type: "button", class: "btn" }, "Waive");
  waive.addEventListener("click", () => {
    const why = area("Why is it waived?", "");
    const go = el("button", { type: "button", class: "btn pri" }, "Waive");
    const m = openModal({
      title: `Waive ${d.name}`,
      sub: "A waiver needs a reason.",
      body: [el("div", { class: "form", style: "max-width:none" }, why.el)],
      actions: [go],
    });
    why.input.focus();
    go.addEventListener("click", async () => {
      go.disabled = true;
      const r = await post("waive", { reason: why.input.value });
      go.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      reviewed(r, "waived");
    });
  });

  const openPerson = el(
    "button",
    { type: "button", class: "btn" },
    icon("arrow-right"),
    ` Open ${v.person.name}`,
  );
  openPerson.addEventListener("click", () => v.person.path && ctx.go(v.person.path));

  const file = d.file;
  const preview = el(
    "button",
    {
      type: "button",
      class: "docimg",
      "aria-label": file?.id ? `View ${file.name}` : "No file",
    },
    icon("file-media"),
    el("span", {}, file ? file.name : "Nothing uploaded"),
  );
  preview.addEventListener(
    "click",
    () => file?.id && void openViewer({ file: { id: file.id, name: file.name } }),
  );

  const fields = el(
    "div",
    { class: "form", style: "max-width:none" },
    note,
    el(
      "div",
      { class: "formgrid" },
      number.el,
      issued.el,
      expires.el,
      result ? result.el : el("div"),
    ),
    el("p", { class: "small muted" }, "AI reading is off. Type the details from the image."),
    d.restricted && !v.seeResults
      ? el(
          "p",
          { class: "note" },
          "Only people who can see background and drug results review this one. Its status shows here.",
        )
      : null,
  );

  // Brief 1E-32: every card on the document folds.
  const page = el("div", { class: "record" });
  foldRecord(page, "document");
  host.append(page);
  page.append(
    el(
      "div",
      { class: "head" },
      el(
        "div",
        { class: "t" },
        el("div", { class: "eyebrow" }, "Document to review"),
        el("h1", {}, icon("file-badge", "h1i"), `Uploaded: ${d.name}`),
        el("div", { class: "muted" }, `${v.person.name} · Upload on their record · ${d.uploaded}`),
      ),
      el("div", { class: "actions" }),
    ),
    el(
      "section",
      { class: "card" },
      el(
        "div",
        { class: "cardhead" },
        el("h2", {}, icon("file-badge"), ` ${d.name}`),
        d.waiting ? chip("Waiting for review", "warn") : chip("Reviewed", "ok"),
      ),
      el("div", { class: "docprev" }, preview, fields),
    ),
    el(
      "div",
      { class: "grid2" },
      el(
        "section",
        { class: "card" },
        el(
          "div",
          { class: "linkrow", style: "margin:0 0 10px" },
          chip("Document to review", "warn"),
          v.business
            ? el(
                "span",
                { class: "bizt" },
                icon(
                  v.business === "Transportation"
                    ? "location"
                    : v.business === "Staffing"
                      ? "briefcase"
                      : "heart",
                ),
                ` ${v.business}`,
              )
            : null,
        ),
        el("p", {}, `Uploaded on ${v.person.name}'s record.`),
        file?.id
          ? (() => {
              const b = el(
                "button",
                { type: "button", class: "chipbtn" },
                icon("paperclip"),
                ` ${file.name}`,
              );
              b.addEventListener(
                "click",
                () => file.id && void openViewer({ file: { id: file.id, name: file.name } }),
              );
              return el(
                "div",
                { class: "linkrow" },
                b,
                sendButton({ files: [{ id: file.id, name: file.name }], document: v.doc.name }),
              );
            })()
          : null,
      ),
      el(
        "section",
        { class: "card aisug off" },
        el(
          "div",
          { class: "actions" },
          ...(v.canReview && d.waiting ? [approve, back, waive] : []),
          ...(v.person.path ? [openPerson] : []),
        ),
      ),
    ),
  );
}
